import React from "react";
import { CheckSquare, Layers } from "lucide-react";
import { t } from "../../locales";
import { MIN_LEFTOVER_INCHES } from "../../static/pipe";
import { formatSize } from "../../lib/utils";
import { Label } from "../ui/label";
import { MaterialGroupCalc } from "../../hooks/useCuttingMaterialGroups";

type Props = {
  materialGroups: MaterialGroupCalc[];
  getAvailableLeftoversForGroup: (sizeId: string) => { id: number; remaining_inches: number; pipe_size_name: string }[];
  selectedLeftoversByGroup: Record<string, number[]>;
  onToggleLeftover: (sizeIdStr: string, leftoverId: number) => void;
};

// The Material Groups UI for a cutting process order: the predicted
// new-pipes/leftover breakdown per item (pipe length = the group's own
// material size — a material's size IS its raw-material stock length, not a
// separately picked value), and the pool of existing leftover pipes the user
// can opt into consuming. All calculation lives in useCuttingMaterialGroups
// — this component only renders it.
const MaterialGroupsSection = ({
  materialGroups,
  getAvailableLeftoversForGroup,
  selectedLeftoversByGroup,
  onToggleLeftover,
}: Props) => {
  if (materialGroups.length === 0) return null;

  return (
    <>
      <div className="border-t-2 border-[#113F67]/10" />
      <div>
        <div className="mb-4">
          <Label className="text-lg font-bold text-[#113F67] flex items-center gap-2">
            <Layers className="h-5 w-5" />
            {t.processOrders.materialGroupTitle}
          </Label>
          <p className="text-sm text-gray-500 mt-1">{t.processOrders.materialGroupDescription}</p>
        </div>

        {materialGroups.map((mg) => {
          const sizeIdStr = String(mg.sizeId);
          const availableLeftovers = getAvailableLeftoversForGroup(sizeIdStr);
          const currentSelections = selectedLeftoversByGroup[sizeIdStr] || [];

          return (
            <div
              key={mg.sizeId}
              className="mb-4 rounded-xl p-4 md:p-5 bg-emerald-50/50 border-l-4 border-emerald-500 shadow-sm"
            >
              <h3 className="text-base font-bold text-emerald-800 mb-3 flex items-center gap-2">
                <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold">
                  {mg.sizeName}
                </span>
                <span className="text-sm font-normal text-gray-500">
                  ({mg.itemDetails.length} {t.processOrders.itemsCount})
                </span>
              </h3>

              <div className="flex items-center gap-6 mb-4">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">{t.processOrders.totalPipesNeeded}</p>
                  <p className="text-2xl font-bold text-emerald-700">{mg.totalFreshPipes}</p>
                </div>
                {mg.finalLeftover >= MIN_LEFTOVER_INCHES && (
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">{t.processOrders.leftoverLabel}</p>
                    <p className="text-lg font-bold text-amber-600">{formatSize(mg.finalLeftover)}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2 mb-3">
                {mg.itemDetails.map((detail) => (
                  <div key={detail.index} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#113F67] text-white text-xs font-bold">
                        {detail.index + 1}
                      </span>
                      <span className="font-medium text-gray-800">{detail.itemName}</span>
                      {detail.cuttingSizeMm > 0 && (
                        <span className="text-gray-500">({formatSize(detail.cuttingSizeMm)})</span>
                      )}
                      <span className="text-gray-500">x {detail.qty} {t.processOrders.nangUnit}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      {detail.piecesFromLeftover > 0 && (
                        <span className="text-green-600 font-medium">
                          {t.processOrders.fromLeftover.replace("{n}", String(detail.piecesFromLeftover))}
                        </span>
                      )}
                      {detail.freshPipes > 0 && (
                        <span className="text-[#113F67] font-medium">
                          {t.processOrders.newPipes.replace("{n}", String(detail.freshPipes))}
                        </span>
                      )}
                      {detail.freshPipes === 0 && detail.piecesFromLeftover > 0 && (
                        <span className="text-green-600 font-medium">
                          {t.processOrders.noNewPipesNeeded}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {mg.savedPieces.length > 0 && (
                <div className="p-3 mb-3 bg-amber-50/70 rounded-lg border border-amber-200/60">
                  <p className="text-xs font-semibold text-amber-800 mb-2">
                    {t.processOrders.savedPiecesLabel.replace("{n}", String(mg.savedPieces.length))}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(
                      mg.savedPieces.reduce<Record<string, number>>((acc, v) => {
                        const key = String(v);
                        acc[key] = (acc[key] || 0) + 1;
                        return acc;
                      }, {})
                    )
                      .map(([v, n]) => ({ inches: Number(v), count: n }))
                      .sort((a, b) => b.inches - a.inches)
                      .map((p, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-amber-300/60 text-xs text-amber-900"
                        >
                          {p.count} × <strong className="ml-1">{formatSize(p.inches)}</strong>
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {availableLeftovers.length > 0 && (
                <div className="p-3 bg-blue-50/70 rounded-lg border border-blue-200/50">
                  <p className="text-xs font-semibold text-[#113F67] mb-2 flex items-center gap-1">
                    <CheckSquare className="h-3.5 w-3.5" />
                    {t.processOrders.availableLeftoverPipes}
                  </p>
                  {availableLeftovers.map((lo) => (
                    <label
                      key={lo.id}
                      className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-blue-100/50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-2 border-[#113F67] text-[#113F67] focus:ring-[#113F67]"
                        checked={currentSelections.includes(lo.id)}
                        onChange={() => onToggleLeftover(sizeIdStr, lo.id)}
                      />
                      <span className="text-sm text-gray-700">
                        {t.processOrders.leftoverFromPreviousOrder} <strong className="text-[#113F67]">{formatSize(lo.remaining_inches)}</strong>
                        <span className="text-gray-400 ml-1">• {lo.pipe_size_name}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default MaterialGroupsSection;
